<template>
  <div class="content">
    <div class="content-wrap">
      <!-- Nagłówek formularza -->
      <h3>{{ t('user-edit').toUpperCase() }}</h3>
      <div class="card-body">
        <!-- Formularz z walidacją (vee-validate) -->
        <Form
          @submit="onSubmit"
          :validation-schema="schema"
          v-slot="{ errors, isSubmitting }"
          :keepValues="false"
        >
          <div class="column d-flex justify-content-center user-data">
            <!-- LEWA KOLUMNA -->
            <div class="col-sm-6">
              <!-- Pole: nazwa użytkownika -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('username') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="userName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.userName }"
                    v-model="userData.username"
                  />
                  <div class="invalid-feedback">{{ errors.userName }}</div>
                </div>
              </div>
              <!-- Pole: imię -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('name') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="firstName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.firstName }"
                    v-model="userData.firstName"
                  />
                  <div class="invalid-feedback">{{ errors.firstName }}</div>
                </div>
              </div>
              <!-- Pole: nazwisko -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('lastname') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="lastName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.lastName }"
                    v-model="userData.lastName"
                  />
                  <div class="invalid-feedback">{{ errors.lastName }}</div>
                </div>
              </div>
              <!-- Pole: email -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('email-address') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="email"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.email }"
                    v-model="userData.email"
                  />
                  <div class="invalid-feedback">{{ errors.email }}</div>
                </div>
              </div>
              <!-- Telefon - kierunkowy i nr główny -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('phone') }}:</label>
                <div class="col-sm-2">
                  <!-- Kierunkowy (readonly) -->
                  <Field
                    name="phoneNumberDir"
                    type="text"
                    class="form-control"
                    :disabled="true"
                    v-model="userData.phoneNumberDir"
                  />
                </div>
                <div class="col-sm-6">
                  <!-- Numer główny -->
                  <Field
                    name="phoneNumber"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.phoneNumber }"
                    v-model="userData.phoneNumber"
                  />
                  <div class="invalid-feedback">
                    {{ errors.phoneNumber }}
                  </div>
                </div>
              </div>
            </div>
            <!-- PRAWA KOLUMNA -->
            <div class="col-sm-6">
              <!-- Pole: hasło (opcjonalne) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('password') }}:</label>
                <div class="col-sm-8">
                  <Field
                      name="password"
                      type="password"
                      class="form-control"
                      :class="{ 'is-invalid': errors.password }"
                      v-model="userData.password"
                      autocomplete="new-password"
                  />
                  <div class="invalid-feedback">{{ errors.password }}</div>
                </div>
              </div>

              <!-- Pole: powtórz hasło -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('repeat-password') }}:</label>
                <div class="col-sm-8">
                  <Field
                      name="confirmPassword"
                      type="password"
                      class="form-control"
                      :class="{ 'is-invalid': errors.confirmPassword }"
                      v-model="userData.confirmPassword"
                      autocomplete="new-password"
                  />
                  <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
                </div>
              </div>
              <!-- Uprawnienia (rola) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('permissions') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="roles"
                    as="select"
                    class="form-control"
                    :class="{ 'is-invalid': errors.roles }"
                    v-model="selectedRoleId"
                  >
                    <!--
                      Grupy zawierające ROLE_13/14/15 są ukryte dla adminów, którzy
                      sami nie posiadają tych uprawnień — żeby nie dało się eskalować
                      uprawnień przez wybór gotowej grupy. Wyjątek: aktualnie przypisana
                      grupa (currentRoleId) jest zawsze pokazana, żeby nie znikała przy
                      edycji.
                    -->
                    <option
                      v-for="role in assignableRoles"
                      :key="role.id"
                      :value="role.id"
                    >
                      {{ role.name }}
                    </option>
                  </Field>
                  <div class="invalid-feedback">{{ errors.roles }}</div>
                </div>
              </div>
              <!-- Opis -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="description"
                    type="textarea"
                    class="form-control"
                    v-model="userData.description"
                  />
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>
              </div>
              <!-- Status: aktywny/nieaktywny (checkbox) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">Status:</label>
                <div class="col-sm-8">
                  <Field
                    name="status"
                    type="checkbox"
                    :value="true"
                    v-model="userData.enabled"
                  />
                  <div class="invalid-feedback">{{ errors.status }}</div>
                </div>
              </div>
              <!-- Grupy kontaktów listy poszukiwanych (członkostwo read-only) -->
              <div class="form-group row" v-if="contactGroupsAvailable.length > 0">
                <label class="col-sm-4 col-form-label">{{ t('user-contact-groups') }}:</label>
                <div class="col-sm-8">
                  <select multiple class="form-control contact-groups-select" v-model="selectedContactGroupIds">
                    <option v-for="group in contactGroupsAvailable" :key="group.id" :value="group.id">
                      {{ group.name }}
                    </option>
                  </select>
                  <small class="form-text text-muted">{{ t('user-contact-groups-hint') }}</small>
                </div>
              </div>
            </div>
          </div>
          <!-- Przycisk Zapisz i Anuluj + komunikaty -->
          <div class="form-group row d-flex flex-column align-items-center actions">
            <div class="col-auto" v-if="processError">
              <div class="process-error form-control">{{ processError }}</div>
            </div>
            <div class="col-auto" v-if="processSuccess">
              <div class="process-success form-control">
                {{ processSuccess }} a
              </div>
            </div>
            <div class="col-auto buttons">
              <button
                type="button"
                @click="goBack"
                class="btn button-add-user"
              >
                Anuluj
              </button>
              <button
                class="btn button-save"
                :disabled="isSubmitting"
              >
                <span
                  v-show="isSubmitting"
                  class="spinner-border spinner-border-sm mr-1"
                ></span>
                {{ t('save').toUpperCase() }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useI18n } from "vue-i18n";

moment.locale("pl");

const { t } = useI18n();

// Yup - schemat walidacji (dynamicznie tłumaczony!)
const schema = Yup.object().shape({
  userName: Yup.string().required(t('user-username-required')),
  firstName: Yup.string().required(t('user-name-required')),
  lastName: Yup.string().required(t('user-lastname-required')),
  email: Yup.string()
    .required(t('user-email-required'))
    .email(t('incorrect-email-address')),
  phoneNumber: Yup.string().required(t('user-phone-required')),
  roles: Yup.string().required(t('user-permissions-required')),
  description: Yup.string(),
  status: Yup.bool(),
  password: Yup.string(),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref('password'), null],
    t('passwords-are-not-the-same')
  ),
});
</script>

<script>
import { ref } from "vue";
import Users from "@/services/Users";
import ContactGroupsService from "@/services/ContactGroupsService";
import { useUserStore } from "@/stores/user.store";

export default {
  props: {
    userId: { required: true }, // ID edytowanego użytkownika
  },
  name: "EditUser",
  computed: {
    // Store przez getter — w SFC z `<script setup>` druga sekcja `<script>` (Options API)
    // nie wykonuje swojego `setup()`, więc zwrócenie store'a tą drogą daje `undefined`
    // w `this`. Computed wywoła `useUserStore()` przy każdym dostępie (singleton).
    userStore() {
      return useUserStore();
    },
    // Grupy ról, do których admin może przypisać użytkownika. Wyklucza grupy
    // zawierające uprawnienia chronione (ROLE_13/14/15), jeśli admin sam ich nie posiada.
    // Aktualnie wybrana grupa (selectedRoleId) jest zawsze widoczna — żeby formularz
    // nie tracił przypisania przy edycji innych pól.
    assignableRoles() {
      return (this.rolesAvailable || []).filter((role) => {
        if (Number(role?.id) === Number(this.selectedRoleId)) {
          return true;
        }
        return this.userStore.canAssignRoleGroup(role);
      });
    },
  },
  data() {
    return {
      loading: false, // do spinnerów
      users: [], // nieużywane tu
      rolesAvailable: [], // lista ról do wyboru
      contactGroupsAvailable: [], // grupy kontaktów do przypisania
      selectedContactGroupIds: [], // wybrane grupy (członkostwo)
      error: null,
      moment: moment,
      getPasswordLength: 14, // do wyświetlania/obsługi długości hasła?
      processError: false, // komunikat błędu zapisu
      processSuccess: false, // komunikat sukcesu
      userData: {}, // główny obiekt formularza (zassany z API)
      form: {
        password: ref(""),
        confirmPassword: ref(""),
        proposed: ref(""),
        userName: ref(""),
      },
      selectedRoleId: null, // wybrana rola (pojedyncza)
    };
  },
  created() {
    // Pobierz role z API i zapisz je do rolesAvailable
    Users.getRolesList().then((data) => {
      for (const item in data) {
        this.rolesAvailable.push(data[item]);
      }
    });

    // Pobierz grupy kontaktów listy poszukiwanych do przypisania
    ContactGroupsService.getAll()
        .then((groups) => {
          this.contactGroupsAvailable = Array.isArray(groups) ? groups : [];
        })
        .catch((error) => {
          console.error("Error fetching contact groups:", error);
          this.contactGroupsAvailable = [];
        });

    // Pobierz dane użytkownika do edycji
    Users.fetchUser(this.userId)
        .then((data) => {
          this.userData = data;

          // Wstępne zaznaczenie grup kontaktów, których użytkownik jest członkiem.
          this.selectedContactGroupIds = Array.isArray(data.memberWantedListContactGroupIds)
            ? data.memberWantedListContactGroupIds.map(Number)
            : [];

          // Rozdziel numer telefonu na kierunkowy i numer
          if (data.phoneNumber && data.phoneNumber.startsWith("+48")) {
            this.userData.phoneNumberDir = "+48";
            this.userData.phoneNumber = data.phoneNumber.slice(3);
          } else {
            this.userData.phoneNumberDir = "+48";
            this.userData.phoneNumber = data.phoneNumber || "";
          }

          // Predefiniuj hasło (nie wyświetlamy prawdziwego!)
          this.userData.password = "";
          this.userData.confirmPassword = ""; // <-- tutaj
          this.selectedRoleId = data.roles[0]?.id || null;

          this.userData.permissions = data.roles
              .map((role) => role.name)
              .join(",");
          this.form.userName = data.username;
        })
        .catch((error) => {
        console.log(error);
        this.processError = error;
      });
  },
  methods: {
    goBack() {
      this.$emit("goBack"); // powrót do listy użytkowników
    },
    onSubmit(values) {
      this.processSuccess = null;
      this.processError = null;

      // Jeśli użytkownik nie zmienił hasła - nie nadpisuj hasła w API
      const newPassword = values.password?.trim();

      // Złóż pełny numer telefonu do API
      const fullPhoneNumber =
        (this.userData.phoneNumberDir || "+48") + (values.phoneNumber || "");

      // Zbuduj obiekt do wysłania do API
      const sendUser = {
        username: values.userName,
        password: newPassword ? newPassword : null,
        enabled: values.status ? values.status : false,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: fullPhoneNumber,
        company: "",
        description: values.description,
        sendUserLogsToServer: this.userData.sendUserLogsToServer,
        roles: [this.selectedRoleId], // Tylko jeden ID roli!
        wantedContactGroupIds: this.selectedContactGroupIds.map(Number),
      };

      // Aktualizuj użytkownika
      Users.updateUser(this.userId, sendUser)
        .then(() => {
          this.goBack();
        })
        .catch((error) => {
          console.log(error);
          this.processError = error;
        });
    },
  },
};
</script>

<style scoped>
.col-form-label {
  text-align: right;
}
.process-error {
  color: darkred;
}

.process-success {
  color: #42b983;
}

.content {
  width: 100%;
  min-height: 270px;
}

h3 {
  padding: 15px 25px;
  background-color: white;
}

.card-body .column {
  padding: 20px 30px;
}

.card-body .column > div {
  padding: 5px 20px;
}
.user-table {
  width: 100%;
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 19px;
  color: black;
}

.user-table th {
  background: white;
  font-size: 16px;
  color: black;
  border-left: 1px solid rgb(214, 214, 215);
  border-bottom: 1px solid rgb(214, 214, 215);
}

.user-table td {
  background: rgb(214, 214, 215);
  font-size: 14px;
  color: dimgrey;
  border-left: 1px solid black;
}
.actions {
  margin: 10px 0;
}
.actions button {
  margin: 0 5px;
}
.user-data {
  border-bottom: 1px solid #b8b8b6;
}
.invalid-feedback {
  font-size: 11px;
}
input[type="checkbox"] {
  margin-top: 13px;
}

.buttons {
  margin-top: .25rem;
  margin-bottom: 1rem;
}

.buttons button {
  padding: 0.5rem 2rem 0.5rem 2rem;
}

.button-save {
  border: 1px solid #007e1a !important;
  border-radius: 4px;
}

.button-save:hover {
  background-color: #005511 !important;
  border: 1px solid #005511 !important;
}

.button-add-user {
  border-radius: 4px;
}

.button-add-user:hover {
  background-color: #c9c8c8;
}
</style>
